import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAssessment } from '../context/AssessmentContext';
import { checkConnection } from '../supabaseClient';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiUsers, FiTrendingUp, FiCheckCircle, FiBriefcase, FiAlertTriangle, FiWifi, FiWifiOff } = FiIcons;

function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, dispatch } = useAssessment();
  const [dbStatus, setDbStatus] = useState('checking');
  
  const [formData, setFormData] = useState({
    name: state.userInfo.name || '',
    email: state.userInfo.email || '',
    orgCode: state.userInfo.orgCode || ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Check DB Connection on load
    const verifyConnection = async () => {
      const status = await checkConnection();
      setDbStatus(status.connected ? 'online' : 'offline');
    };
    verifyConnection();

    const codeFromUrl = searchParams.get('orgCode');
    if (codeFromUrl) {
      setFormData(prev => ({ ...prev, orgCode: codeFromUrl }));
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    return newErrors;
  };

  const handleStartAssessment = () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    const userProvidedCode = formData.orgCode.trim().length > 0;
    const finalOrgCode = userProvidedCode ? formData.orgCode.trim() : `ORG-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    
    dispatch({ 
      type: 'SET_USER_INFO', 
      payload: { ...formData, orgCode: finalOrgCode, userProvidedCode } 
    });
    navigate('/assessment');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      {/* Connection Indicator */}
      <div className="fixed top-4 right-4 z-50">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold shadow-sm border ${
          dbStatus === 'online' ? 'bg-green-50 text-green-700 border-green-200' : 
          dbStatus === 'offline' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-50 text-gray-500 border-gray-200'
        }`}>
          <SafeIcon icon={dbStatus === 'online' ? FiWifi : dbStatus === 'offline' ? FiWifiOff : FiTrendingUp} />
          {dbStatus === 'online' ? 'Database Connected' : dbStatus === 'offline' ? 'External DB Offline' : 'Checking Connection...'}
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-800 mb-4">Wendworks<sup>®</sup></h1>
          <h2 className="text-3xl font-semibold text-blue-600 mb-2">Organizational Health Assessment</h2>
          <p className="text-xl text-gray-600">Securely evaluate your organization's health across 8 pillars.</p>
        </div>

        {dbStatus === 'offline' && (
          <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 flex items-start gap-3">
            <SafeIcon icon={FiAlertTriangle} className="shrink-0 mt-1" />
            <div>
              <p className="font-bold">Database Connection Note</p>
              <p className="text-sm">We're having trouble connecting to the central database. You can still complete the assessment, and your report will be generated locally in your browser!</p>
            </div>
          </div>
        )}

        <motion.div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Get Started</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`} />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`} />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Org Code (Optional)</label>
              <input type="text" name="orgCode" value={formData.orgCode} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g. WEND-2024" />
            </div>
            <button onClick={handleStartAssessment} className="w-full mt-6 bg-blue-600 text-white font-semibold py-4 px-6 rounded-lg hover:bg-blue-700 transition-all shadow-lg active:scale-95">
              Start Assessment
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default LandingPage;